import { useEffect, useState } from "react";
import api from "../api/api";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

export default function Orders() {
  const emptyForm = {
    contractor_id: "",
    quantity: "",
    service_date: "",
    pickup_remarks: "",
  };

  const [orders, setOrders] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [filterID, setFilterID] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});
  const [backendError, setBackendError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [filters, setFilters] = useState({
    id: "",
    name: "",
    city: "",
    street: "",
    zip: "",
    quantity: "",
    date: "",
    status: "",
  });

  const navigate = useNavigate();

  const btn = {
    primary:
      "bg-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-blue-700 transition shadow-sm",
    success:
      "bg-green-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-green-700 transition shadow-sm",
    danger:
      "bg-red-500 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-red-600 transition shadow-sm",
    ghost:
      "bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded hover:bg-gray-300 transition",
  };

  const statusOptions = [
    { id: 1, name: "Utworzone" },
    { id: 2, name: "Zaplanowane" },
    { id: 3, name: "Odebrane" },
    { id: 4, name: "Zakończone" },
  ];

  useEffect(() => {
    fetchOrders();
    fetchContractors();
    fetchStatuses();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await api.get("/orders");
      setOrders(res.data);
    } catch (err) {
      console.error("Błąd pobierania zamówień:", err);
    }
  };

  const fetchContractors = async () => {
    try {
      const res = await api.get("/contractors");
      setContractors(res.data);
    } catch (err) {
      console.error("Błąd pobierania kontrahentów:", err);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await api.get("/statuses");
      setStatuses(res.data);
    } catch (err) {
      console.error("Błąd pobierania statusów:", err);
    }
  };

  const openCreateModal = () => {
    setForm(emptyForm);
    setEditId(null);
    setErrors({});
    setBackendError("");
    setShowEditModal(true);
  };

  const openEditModal = (order) => {
    setForm({
      contractor_id: order.contractor_id,
      quantity: order.quantity,
      service_date: format(parseISO(order.service_date), "yyyy-MM-dd"),
      pickup_remarks: order.pickup_remarks,
    });
    setEditId(order.id);
    setErrors({});
    setBackendError("");
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setForm(emptyForm);
    setEditId(null);
    setErrors({});
    setBackendError("");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.contractor_id) newErrors.contractor_id = "Wybierz kontrahenta";
    if (!form.quantity || Number(form.quantity) < 1)
      newErrors.quantity = "Ilość musi być większa niż 0";
    if (!form.service_date) newErrors.service_date = "Podaj datę realizacji";
    if (form.pickup_remarks && (form.pickup_remarks.length > 256))
      newErrors.pickup_remarks = "Uwagi nie mogą być dłuższe niż 256 znaków";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setBackendError("");
    console.log(form);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editId) {
        await api.put(`/orders/${editId}`, form);
      } else {
        await api.post("/orders", form);
      }
      fetchOrders();
      closeEditModal();
    } catch (err) {
      console.error("Błąd zapisu zamówienia:", err.response?.data || err.message);

      const backendData = err.response?.data;
      if (backendData && typeof backendData === "object") {
        const newFieldErrors = {};
        for (const key in backendData) newFieldErrors[key] = backendData[key];
        setErrors(newFieldErrors);
      } else {
        setBackendError(backendData?.detail || "Wystąpił nieoczekiwany błąd");
      }
    }
  };

  const handleDelete = async (id) => {
    if (!id) return false;
    setIsDeleting(true);
    try {
      await api.delete(`/orders/${id}`);
      fetchOrders();
      return true;
    } catch (err) {
      console.error("Błąd usuwania zamówienia:", err);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = async () => {
    await handleDelete(deleteId);
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDestruction = async (id) => {
    try {
      await api.put(`/orders/${id}`, { status_id: 4 });
      await api.put(`/document/send/certificate/${id}`);
      fetchOrders();
    } catch (err) {
      console.error("Błąd potwierdzenia zniszczenia:", err);
    }
  };

  const contractorOptions = contractors.map((c) => ({
    value: c.id,
    label: `${c.name} [${c.street}]`,
    contactPerson: c.contact_person,
    contractor: c,
  }));

  const filteredOrders = orders.filter((o) => {
    const contractor = contractors.find((c) => c.id === o.contractor_id);

    const matchesId = o.id.toString().includes(filters.id || "");
    const matchesName = (contractor?.name || "")
      .toLowerCase()
      .includes(filters.name.toLowerCase());

    const matchesCity = (contractor?.city || "")
      .toLowerCase()
      .includes(filters.city.toLowerCase());

    const matchesStreet = (contractor?.street || "")
      .toLowerCase()
      .includes(filters.street.toLowerCase());

    const matchesZip = (contractor?.zip_code || "")
      .toLowerCase()
      .includes(filters.zip.toLowerCase());

    const matchesQty = o.quantity
      .toString()
      .includes(filters.quantity || "");

    const matchesDate = filters.date
      ? format(parseISO(o.service_date), "yyyy-MM-dd") === filters.date
      : true;

    const matchesStatus = filters.status
      ? String(o.status_id) === String(filters.status)
      : true;

    return (
      matchesId &&
      matchesName &&
      matchesCity &&
      matchesStreet &&
      matchesZip &&
      matchesQty &&
      matchesDate &&
      matchesStatus
    );
  });

  return (
    <div className="p-3 space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={openCreateModal} className={btn.primary}>
          + Dodaj nowe zlecenie
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-600">
              <tr className="text-left text-xs font-semibold">
                <th className="px-2 py-3 align-bottom">Numer</th>
                <th className="px-2 py-3 align-bottom">Nazwa</th>
                <th className="px-2 py-3 align-bottom">Miasto</th>
                <th className="px-2 py-3 align-bottom">Ulica</th>
                <th className="px-2 py-3 align-bottom">Kod</th>
                <th className="px-2 py-3 align-bottom">Ilość</th>
                <th className="px-2 py-3 align-bottom">Data</th>
                <th className="px-2 py-3 align-bottom">Status</th>
                <th className="px-2 py-3 align-bottom text-center">Dokument</th>
                <th className="px-2 py-3 align-bottom text-center">Certyfikat</th>
                <th className="px-2 py-3 align-bottom text-center">Akcje</th>
              </tr>

              <tr className="bg-white text-xs border-t border-gray-200">
                {[
                  { key: "id", placeholder: "Numer...", type: "text" },
                  { key: "name", placeholder: "Nazwa...", type: "text" },
                  { key: "city", placeholder: "Miasto...", type: "text" },
                  { key: "street", placeholder: "Ulica...", type: "text" },
                  { key: "zip", placeholder: "Kod...", type: "text" },
                  { key: "quantity", placeholder: "Ilość...", type: "text" },
                  { key: "date", placeholder: "", type: "date" },
                ].map((f) => (
                  <th key={f.key} className="py-2 align-top">
                    <div className="px-2">
                      <input
                        type={f.type}
                        placeholder={f.placeholder}
                        value={filters[f.key]}
                        onChange={(e) =>
                          setFilters({ ...filters, [f.key]: e.target.value })
                        }
                        className="w-full h-8 border border-gray-200 rounded-md px-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </th>
                ))}

                <th className="py-2 align-top">
                  <div className="px-1">
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Wszystkie</option>
                      {statusOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>

                <th />
                <th />
                <th />
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((o) => {
                const contractor = contractors.find(
                  (c) => c.id === o.contractor_id
                );
                const status = statuses.find((s) => s.id === o.status_id);

                return (
                  <tr
                    key={o.id}
                    className="cursor-pointer transition-colors hover:bg-gray-50 bg-white text-xs"
                  >
                    <td className="px-2 py-3">{o.id}</td>
                    <td className="px-2 py-3 font-medium text-gray-800">{o.contractor_name}</td>
                    <td className="px-2 py-3">{o.city}</td>
                    <td className="px-2 py-3">{o.street}</td>
                    <td className="px-2 py-3">{o.zip_code}</td>
                    <td className="px-2 py-3 text-center">{o.quantity}</td>
                    <td className="px-2 py-3">
                      {format(parseISO(o.service_date), "yyyy-MM-dd")}
                    </td>
                    <td className="px-2 py-3">{status?.name || ""}</td>

                    <td className="px-2 py-3 text-center text-xs">
                      {Number(o.status_id) >= 3 && (
                        <a
                          href={`/api/document/${o.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:scale-110 transition"
                          title="Protokół PDF"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-blue-600 mx-auto"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7 7h10M7 11h10M7 15h10M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"
                            />
                          </svg>
                        </a>
                      )}
                    </td>

                    <td className="px-2 py-3 text-center text-xs">
                      {Number(o.status_id) === 4 && (
                        <a
                          href={`/api/document/certificate/${o.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:scale-110 transition"
                          title="Certyfikat PDF"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-green-600 mx-auto"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12l2 2 4-4m1 10H6a2 2 0 01-2-2V6a2 2 0 012-2h6l6 6v8a2 2 0 01-2 2z"
                            />
                          </svg>
                        </a>
                      )}
                    </td>

                    <td className="px-2 py-3 flex justify-center gap-2">
                      {Number(o.status_id) === 3 && (
                        <button
                          onClick={() => confirmDestruction(o.id)}
                          className={btn.success}
                        >
                          Potwierdź
                        </button>
                      )}
                      {Number(o.status_id) <= 2 && (
                        <button
                          onClick={() => openEditModal(o)}
                          className={btn.primary}
                        >
                          Edytuj
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setDeleteId(o.id);
                          setShowDeleteModal(true);
                        }}
                        className={btn.danger}
                      >
                        Usuń
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>


          {showEditModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">

              <div className="bg-white w-[750px] rounded-lg shadow-xl max-h-[90vh] flex flex-col overflow-hidden">

                <div className="px-6 py-4 border-b bg-gray-50">
                  <h2 className="text-xl font-bold text-gray-800">
                    {editId ? "Edytuj zlecenie" : "Dodaj zlecenie"}
                  </h2>

                  {backendError && (
                    <div className="mt-2 text-red-600 font-medium">
                      {backendError}
                    </div>
                  )}
                </div>

                <div className="p-6 overflow-y-auto">

                  <form onSubmit={handleSubmit} className="flex flex-col gap-3">

                    <div className="flex items-center gap-3">
                      <label className="w-40 font-medium text-gray-700">
                        Kontrahent
                      </label>

                      <div className="flex-1">
                        <Select
                          className="w-full"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          options={contractorOptions}
                          formatOptionLabel={(option) => (
                            <div>
                              <div>{option.label}</div>
                              <div className="text-xs text-gray-500">
                                {option.contactPerson}
                              </div>
                            </div>
                          )}
                          placeholder="Wybierz kontrahenta"
                          isSearchable
                          value={
                            contractorOptions.find(
                              (option) => String(option.value) === String(form.contractor_id)
                            ) || null
                          }
                          onChange={(selected) => {
                            setForm((prev) => ({
                              ...prev,
                              contractor_id: selected?.value || "",
                              pickup_remarks: selected?.contractor.remarks || "",
                            }));
                          }}
                        />
                      </div>
                    </div>

                    {errors.contractor_id && (
                      <span className="text-red-500 text-sm ml-40">
                        {errors.contractor_id}
                      </span>
                    )}

                    <div className="flex items-center gap-3">
                      <label className="w-40 font-medium text-gray-700">
                        Ilość
                      </label>

                      <input
                        type="number"
                        min={1}
                        className={`flex-1 border p-2 rounded-md text-sm h-[38px] focus:outline-none focus:ring-2 ${errors.quantity
                          ? "border-red-500 focus:ring-red-400"
                          : "focus:ring-blue-400"
                          }`}
                        value={form.quantity}
                        onChange={(e) =>
                          setForm({ ...form, quantity: e.target.value })
                        }
                      />
                    </div>

                    {errors.quantity && (
                      <span className="text-red-500 text-sm ml-40">
                        {errors.quantity}
                      </span>
                    )}

                    <div className="flex items-center gap-3">
                      <label className="w-40 font-medium text-gray-700">
                        Data realizacji
                      </label>

                      <input
                        type="date"
                        className={`flex-1 border p-2 rounded-md text-sm h-[38px] focus:outline-none focus:ring-2 ${errors.service_date
                          ? "border-red-500 focus:ring-red-400"
                          : "focus:ring-blue-400"
                          }`}
                        value={form.service_date}
                        onChange={(e) =>
                          setForm({ ...form, service_date: e.target.value })
                        }
                      />
                    </div>

                    {errors.service_date && (
                      <span className="text-red-500 text-sm ml-40">
                        {errors.service_date}
                      </span>
                    )}

                    <div className="flex items-center gap-3">
                      <label className="w-40 font-medium text-gray-700">
                        Uwagi
                      </label>

                      <textarea
                        className={`flex-1 border p-2 rounded-md text-sm focus:outline-none focus:ring-2 ${errors.remarks
                          ? "border-red-500 focus:ring-red-400"
                          : "focus:ring-blue-400"
                          }`}
                        value={form.pickup_remarks}
                        onChange={(e) =>
                          setForm({ ...form, pickup_remarks: e.target.value })
                        }
                        maxLength={256}
                      />
                    </div>

                    {errors.pickup_remarks && (
                      <span className="text-red-500 text-sm ml-40">
                        {errors.pickup_remarks}
                      </span>
                    )}

                  </form>
                </div>

                <div className="px-6 py-4 border-t flex justify-end gap-3 bg-white">

                  <button
                    type="button"
                    onClick={closeEditModal}
                    className={btn.danger}
                  >
                    Anuluj
                  </button>

                  <button
                    onClick={handleSubmit}
                    className={btn.success}
                  >
                    Zapisz
                  </button>

                </div>

              </div>
            </div>
          )}

          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
              <div className="bg-white p-6 w-[500px] rounded shadow-lg relative">
                <p className="mb-4">
                  Czy na pewno chcesz usunąć zlecenie{" "}
                  <span className="font-bold text-red-600">{deleteId}</span>{" "}
                  i wszystkie powiązane z nim elementy?
                </p>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className={btn.ghost}
                  >
                    Anuluj
                  </button>

                  <button
                    onClick={() => confirmDelete(deleteId)}
                    className={btn.danger}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}